import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ArrowBack } from '@mui/icons-material';
import { SxProps, Theme } from '@mui/material';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { AssetsIllustration } from '../../illustrations/form_builder/AssetsIllustration';
import { CustomListIllustration } from '../../illustrations/form_builder/CustomListIllustration';
import { LocationsIllustration } from '../../illustrations/form_builder/LocationsIllustration';
import { useAssetTypesWithAssetsQuery } from '../../queries/assets';
import { DataListSource, QuestionUnsafe } from '../../types';
import { AssetSummary, AssetTypeWithAssets } from '../../types/asset';
import { datascopeTracker } from '../../utils/eventTracker';
import { useLocationsSelectorState } from '../Locations/LocationsSelector/LocationsSelectorReducer';
import { SelectedStatus } from '../Locations/utils/LocationsReducer';
import { Modal } from '../uiComponents/modal';
import { SearchBar } from '../uiComponents/searchBar';
import { FormBuilderContext } from './FormBuilderProvider';
import { SearchDataListModalActions } from './searchDataListModal/searchDataListModalActions';
import { SearchDataListModalList } from './searchDataListModal/searchDataListModalList';

const customStyles = {
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  backButton: {
    cursor: 'pointer',
  },
} satisfies Record<string, SxProps<Theme>>;

export enum ListTabs {
  CustomList = 'CUSTOM_LISTS',
  Locations = 'LOCATIONS',
  Assets = 'ASSETS',
}

export type Category = {
  id: ListTabs;
  title: string;
  description: string;
  icon: JSX.Element;
  disabled?: boolean;
  disabledTooltip?: string;
};

interface SearchDataListModalProps {
  dataSources: DataListSource[];
  onSelect: (id: string, value: string, tableId?: string) => void;
  questions: QuestionUnsafe[];
  onNestedLocationsChange: (id: string, selectedLocations: Map<number, SelectedStatus>) => void;
  onNestedLocationsAllSelected: (id: string, allLocationsSelected: boolean) => void;
  onNestedLocationsInTableChange: (
    id: string,
    selectedLocations: Map<number, SelectedStatus>,
    tableId: string,
  ) => void;
  onSelectedAssetsChange: (
    id: string,
    selectedAssets: { assets: number[]; asset_types: number[] },
  ) => void;
  onSelectedAssetsInTableChange: (
    id: string,
    selectedAssets: { assets: number[]; asset_types: number[] },
    tableId: string,
  ) => void;
}

export const SearchDataListModal = memo(
  ({
    dataSources,
    onSelect,
    questions,
    onNestedLocationsChange,
    onNestedLocationsInTableChange,
    onNestedLocationsAllSelected,
    onSelectedAssetsChange,
    onSelectedAssetsInTableChange,
  }: SearchDataListModalProps) => {
    const lang = useLanguage();
    const { formId } = useParams<{ formId: string }>();
    const {
      searchDataListModalOpen,
      setSearchDataListModalOpen,
      setListModalQuestionId,
      listModalQuestionId,
      setListModalTableId,
      listModalTableId,
    } = useContext(FormBuilderContext);
    const { checkFlag } = useFeatureFlags();

    const dataListsUsedInForm = useMemo(() => {
      const uniqueMetadataSet = questions.reduce<Set<string>>((set, question) => {
        if (question.metadata) {
          set.add(question.metadata);
        }

        if (question.questions) {
          const nestedMetadata = question.questions
            .filter((nestedQuestion) => nestedQuestion.metadata)
            .map((nestedQuestion) => nestedQuestion.metadata);
          nestedMetadata.forEach((metadata) => set.add(metadata));
        }

        return set;
      }, new Set<string>());

      return Array.from(uniqueMetadataSet);
    }, [questions]);

    const sourcesInForm = useMemo(() => {
      return dataSources.filter(({ value }) =>
        dataListsUsedInForm.some((sourceVal) => sourceVal === value),
      );
    }, [dataSources, dataListsUsedInForm]);

    const orderSources = useMemo(() => {
      const sourcesWithoutFirst = dataSources
        .slice(1)
        .filter(({ value }) => !sourcesInForm.some((source) => source.value === value));
      const ordered = sourcesWithoutFirst.sort((a, b) => a.tag.localeCompare(b.tag));
      return [dataSources[0], ...ordered];
    }, [dataSources, sourcesInForm]);

    const [selected, setSelected] = useState('');
    const [dataSourcesList, setDataSourcesList] = useState(orderSources);
    const [dataSourcesInForm, setDataSourcesInForm] = useState(sourcesInForm);
    const [searchQuery, setSearchQuery] = useState('');

    // Asset selection state:
    // selectedAssetTypeIds: whole asset types selected (future assets auto-included)
    // selectedAssetIds: individually selected assets (not tied to a full type selection)
    // expandedAssetTypeIds: which asset type rows are expanded to show their assets
    const [selectedAssetTypeIds, setSelectedAssetTypeIds] = useState<number[]>([]);
    const [selectedAssetIds, setSelectedAssetIds] = useState<Record<number, number[]>>({});
    const [expandedAssetTypeIds, setExpandedAssetTypeIds] = useState<number[]>([]);

    // Fetch asset type data (empty search = all types) to reconstruct grouped selectedAssetIds on open.
    // React Query caches this, so it shares the same request as SearchDataListModalList.
    const { data: assetTypesData } = useAssetTypesWithAssetsQuery('');

    // Track when modal is opened
    useEffect(() => {
      if (searchDataListModalOpen) {
        datascopeTracker(
          'open_lists_modal',
          {
            form_id: formId,
            open_modal: true,
          },
          'AMP',
        );
      }
    }, [searchDataListModalOpen]);

    useEffect(() => {
      setDataSourcesList(orderSources);
    }, [orderSources]);

    useEffect(() => {
      setDataSourcesInForm(sourcesInForm);
    }, [sourcesInForm]);

    const handleClose = () => {
      setSearchDataListModalOpen(false);
      setSelected('');
      setListModalQuestionId(null);
      setListModalTableId(null);
      setActiveTab(null);
      setSelectedAssetTypeIds([]);
      setSelectedAssetIds({});
      setExpandedAssetTypeIds([]);
      setInitializedForQuestionId(null);
    };

    const handleSearch = (query: string) => {
      setDataSourcesList(
        orderSources.filter(({ tag }) => tag.toLowerCase().includes(query.toLowerCase())),
      );
      setDataSourcesInForm(
        sourcesInForm.filter(({ tag }) => tag.toLowerCase().includes(query.toLowerCase())),
      );
      setSearchQuery(query);
    };

    const handleConfirm = (selectedValue?: string) => {
      // This is to avoid race condition between the selected value and the selected list id
      const selectedListId = selectedValue ?? selected;
      const selectedSource = dataSources.find((source) => source.value === selectedListId);

      datascopeTracker(
        'apply_selection',
        {
          form_id: formId,
          list_id: selectedListId,
          assets_list: activeTab === ListTabs.Locations ? 'locations' : selectedSource?.tag,
        },
        'AMP',
      );

      if (activeTab === ListTabs.Assets) {
        const allSelectedAssetIds = Object.values(selectedAssetIds).flat();
        const payload = {
          assets: allSelectedAssetIds,
          asset_types: selectedAssetTypeIds,
        };
        if (listModalTableId) {
          onSelectedAssetsInTableChange(listModalQuestionId, payload, listModalTableId);
        } else {
          onSelectedAssetsChange(listModalQuestionId, payload);
        }
        handleClose();
        return;
      }

      if (activeTab === ListTabs.Locations) {
        handleNestedLocationsChange();
      } else {
        onSelect(listModalQuestionId, selectedListId, listModalTableId);
      }
      handleClose();
    };

    const handleSelectItem = (value: string) => {
      const selectedSource = dataSources.find((source) => source.value === value);

      datascopeTracker(
        'select_list_items',
        {
          form_id: formId,
          assets_list: selectedSource?.tag,
          list_id: value,
        },
        'AMP',
      );

      setSelected(value);
    };

    const hasNestableLocationsFlag = checkFlag('NestableLocations');

    const categories: Category[] = useMemo(() => {
      const data: Category[] = [
        {
          id: ListTabs.CustomList,
          title: lang.form_builder.customLists,
          description: lang.form_builder.customListsDescription,
          icon: <CustomListIllustration />,
        },
      ];

      if (hasNestableLocationsFlag) {
        data.push({
          id: ListTabs.Locations,
          title: lang.form_builder.locations,
          description: lang.form_builder.locationsDescription,
          icon: <LocationsIllustration />,
        });
      }

      if (dataSources.some((source) => source.value === 'assets')) {
        const totalAssets = assetTypesData
          ? Object.values(assetTypesData.asset_types_with_assets).reduce(
              (sum, at) => sum + at.assets.length,
              0,
            )
          : 0;
        const hasNoAssets = assetTypesData && totalAssets === 0;
        data.push({
          id: ListTabs.Assets,
          title: lang.mainMenu.assets,
          description: lang.form_builder.assetsDescription,
          icon: <AssetsIllustration />,
          disabled: hasNoAssets,
          disabledTooltip: hasNoAssets ? lang.assets.noAssetPermissions : undefined,
        });
      }

      return data;
    }, [hasNestableLocationsFlag, dataSourcesList, assetTypesData]);

    useEffect(() => {
      /* If there is only one category, set the active tab to CUSTOM_LISTS to improve UX */
      if (categories.length === 1) {
        setActiveTab(ListTabs.CustomList);
      }
    }, [categories.length]);

    const currentQuestion = useMemo(() => {
      if (!listModalQuestionId) return null;

      // First check top-level questions
      const topLevelQuestion = questions.find((q) => q.id === listModalQuestionId);
      if (topLevelQuestion) return topLevelQuestion;

      // Then check nested questions in tables
      for (const question of questions) {
        if (question.questions) {
          const nestedQuestion = question.questions.find((q) => q.id === listModalQuestionId);
          if (nestedQuestion) return nestedQuestion;
        }
      }

      return null;
    }, [listModalQuestionId, questions]);

    // Tracks which question ID has already been initialized, so that adding assetTypesData
    // to the dependency array doesn't re-initialize state after the user has made changes.
    const [initializedForQuestionId, setInitializedForQuestionId] = useState<string | null>(null);

    // Restore saved asset selection when the modal opens for a question that already has selectedAssets.
    // selectedAssetIds is Record<assetTypeId, assetId[]>, but the saved payload stores a flat assets array.
    // We cross-reference the loaded asset type data to re-group individual asset IDs by their type.
    // assetTypesData is included as a dependency because on first open it may not be cached yet —
    // this effect re-runs when it arrives and completes initialization if it hasn't happened yet.
    useEffect(() => {
      if (!searchDataListModalOpen || !currentQuestion?.selectedAssets) return;
      if (initializedForQuestionId === currentQuestion.id) return;
      if (!assetTypesData) return;

      const { asset_types, assets } = currentQuestion.selectedAssets;

      setSelectedAssetTypeIds((asset_types ?? []).map(Number));

      if ((assets ?? []).length > 0) {
        const savedAssetIds = new Set((assets ?? []).map(Number));
        const grouped: Record<number, number[]> = {};
        Object.values(assetTypesData.asset_types_with_assets).forEach((assetType) => {
          const matching = assetType.assets.filter((a) => savedAssetIds.has(a.id)).map((a) => a.id);
          if (matching.length > 0) {
            grouped[assetType.id] = matching;
          }
        });
        setSelectedAssetIds(grouped);
      } else {
        setSelectedAssetIds({});
      }

      setInitializedForQuestionId(currentQuestion.id);
    }, [searchDataListModalOpen, currentQuestion, assetTypesData]);

    const { locationsState, handleChangeLocationSelection, handleChangeAllLocationsSelection } =
      useLocationsSelectorState({
        initialSelectedLocations: currentQuestion?.nestableLocations?.selectedLocations,
        initialAllLocationsSelected: currentQuestion?.nestableLocations?.all_locations_selected,
        multipleSelection: true,
      });

    const [activeTab, setActiveTab] = useState<ListTabs | null>(null);

    const handleNestedLocationsChange = () => {
      if (!listModalQuestionId) return;
      if (locationsState.allLocationsSelected)
        return onNestedLocationsAllSelected(listModalQuestionId, true);

      if (listModalTableId) {
        onNestedLocationsInTableChange(
          listModalQuestionId,
          locationsState.selectedLocations,
          listModalTableId,
        );
      } else {
        onNestedLocationsChange(listModalQuestionId, locationsState.selectedLocations);
      }
    };

    const anyLocationSelected =
      Array.from(locationsState.selectedLocations.values()).some((status) =>
        ['selected', 'onlyChildren'].includes(status),
      ) || locationsState.allLocationsSelected;

    // Asset Types Selection Handlers

    // Toggle expansion of an asset type row
    const toggleAssetTypeExpansion = (assetTypeId: number) => {
      setExpandedAssetTypeIds((prev) =>
        prev.includes(assetTypeId)
          ? prev.filter((id) => id !== assetTypeId)
          : [...prev, assetTypeId],
      );
    };

    // Toggle the whole asset type (adds to selectedAssetTypeIds, visually selects all assets)
    // Deselecting a whole type also clears any individual asset selections for it
    const toggleAssetType = (assetTypeId: number) => {
      const isCurrentlySelected = selectedAssetTypeIds.includes(assetTypeId);
      if (isCurrentlySelected) {
        setSelectedAssetTypeIds((prev) => prev.filter((id) => id !== assetTypeId));
        setSelectedAssetIds((prev) => {
          const next = { ...prev };
          delete next[assetTypeId];
          return next;
        });
      } else {
        // Selecting the whole type: add to type list, clear individual asset selections for it
        setSelectedAssetTypeIds((prev) => [...prev, assetTypeId]);
        setSelectedAssetIds((prev) => {
          const next = { ...prev };
          delete next[assetTypeId];
          return next;
        });
      }
    };

    // Toggle a single asset within a type
    // If all assets end up selected individually, promote to a full type selection
    // If the type was fully selected, demote to individual selection (minus this asset)
    const toggleAsset = (assetTypeId: number, assetId: number, allAssets: AssetSummary[]) => {
      const typeIsFullySelected = selectedAssetTypeIds.includes(assetTypeId);

      if (typeIsFullySelected) {
        // Demote: remove the type, select all other assets individually
        setSelectedAssetTypeIds((prev) => prev.filter((id) => id !== assetTypeId));
        setSelectedAssetIds((prev) => ({
          ...prev,
          [assetTypeId]: allAssets.map((a) => a.id).filter((id) => id !== assetId),
        }));
        return;
      }

      setSelectedAssetIds((prev) => {
        const current = prev[assetTypeId] ?? [];
        const isSelected = current.includes(assetId);
        const next = isSelected ? current.filter((id) => id !== assetId) : [...current, assetId];

        // If all assets are now individually selected, promote to full type selection
        if (next.length === allAssets.length && allAssets.length > 0) {
          setSelectedAssetTypeIds((prevTypes) => [...prevTypes, assetTypeId]);
          const updated = { ...prev };
          delete updated[assetTypeId];
          return updated;
        }

        return { ...prev, [assetTypeId]: next };
      });
    };

    // Toggle all asset types at once (select all → full type selections; deselect all → clear everything)
    const toggleAllAssetTypes = (allAssetTypes: AssetTypeWithAssets[]) => {
      const allTypeIds = allAssetTypes.map((at) => at.id);
      const allSelected = allTypeIds.every((id) => selectedAssetTypeIds.includes(id));

      if (allSelected) {
        setSelectedAssetTypeIds([]);
        setSelectedAssetIds({});
      } else {
        setSelectedAssetTypeIds(allTypeIds);
        setSelectedAssetIds({});
      }
    };

    const isAssetTypeSelected = (assetTypeId: number) => selectedAssetTypeIds.includes(assetTypeId);

    const isAssetSelected = (assetTypeId: number, assetId: number) => {
      if (selectedAssetTypeIds.includes(assetTypeId)) return true;
      return (selectedAssetIds[assetTypeId] ?? []).includes(assetId);
    };

    const getSelectedAssetsCount = (assetTypeId: number, totalAssets: number) => {
      if (selectedAssetTypeIds.includes(assetTypeId)) return totalAssets;
      return (selectedAssetIds[assetTypeId] ?? []).length;
    };

    const areAllAssetTypesSelected = (allAssetTypes: AssetTypeWithAssets[]) => {
      if (allAssetTypes.length === 0) return false;
      return allAssetTypes.every((at) => selectedAssetTypeIds.includes(at.id));
    };

    const anyAssetSelected =
      selectedAssetTypeIds.length > 0 ||
      Object.values(selectedAssetIds).some((ids) => ids.length > 0);

    const confirmButtonDisabled = useMemo(() => {
      if (activeTab === ListTabs.Locations) {
        return !anyLocationSelected;
      }
      if (activeTab === ListTabs.Assets) {
        return !anyAssetSelected;
      }
      return !selected;
    }, [selected, activeTab, anyLocationSelected, anyAssetSelected]);

    return (
      <Modal
        open={searchDataListModalOpen}
        onClose={handleClose}
        titleStartIcon={
          activeTab !== null &&
          categories.length > 1 && (
            <ArrowBack
              sx={customStyles.backButton}
              onClick={() => setActiveTab(null)}
            />
          )
        }
        title={lang.form_builder.dataListSearchModalTitle}
        header={
          activeTab !== null && (
            <SearchBar
              placeholder={lang.form_builder.searchInListPlaceholder}
              onSearch={handleSearch}
            />
          )
        }
        content={
          <SearchDataListModalList
            sourcesList={dataSourcesList}
            onSelect={handleSelectItem}
            selected={selected}
            sourcesInForm={dataSourcesInForm}
            searchQuery={searchQuery}
            locationsState={locationsState}
            handleChangeLocationSelection={handleChangeLocationSelection}
            handleChangeAllLocationsSelection={handleChangeAllLocationsSelection}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            anyLocationSelected={anyLocationSelected}
            categories={categories}
            expandedAssetTypeIds={expandedAssetTypeIds}
            toggleAssetType={toggleAssetType}
            toggleAssetTypeExpansion={toggleAssetTypeExpansion}
            toggleAsset={toggleAsset}
            toggleAllAssetTypes={toggleAllAssetTypes}
            isAssetTypeSelected={isAssetTypeSelected}
            isAssetSelected={isAssetSelected}
            getSelectedAssetsCount={getSelectedAssetsCount}
            areAllAssetTypesSelected={areAllAssetTypesSelected}
          />
        }
        actions={
          activeTab !== null && (
            <SearchDataListModalActions
              onClose={handleClose}
              onConfirm={handleConfirm}
              confirmButtonDisabled={confirmButtonDisabled}
              activeTab={activeTab}
            />
          )
        }
      />
    );
  },
);
